import { ExportSpecModel } from 'app/models/dms/exportSpec.model';
import { Injectable } from '@angular/core';

import { HttpClient, HttpParams } from '@angular/common/http';

import { Observable ,  of } from 'rxjs';

import { CacheService } from './cache.service';

import { ExportPersistType, ExportSpecProvider, ExtenSpecKey, SecurityRight } from 'app/utility/enums';
import { Constants } from 'app/utility/constants';
import { UserService } from './user.service';
import { TabModel } from 'app/models/shared/tab.model';
import { FieldGroupModel } from 'app/models/shared/fieldGroup.model';
import { FieldModel } from 'app/models/shared/field.model';
import { ListItemModel } from 'app/models/shared/listItem.model';
import { MenuService } from './menu.service';

@Injectable({
    providedIn: 'root'
})
export class ExportService
{
    constructor(
        private httpClient: HttpClient,
        private cache: CacheService,
        private userService: UserService,
        private menuService: MenuService,
    )
    { 
        this.menuService.setArea("dms");
    }

    getCachedExportSpecs(filterForAuctions: boolean = false, exportFlagsAllowAuctions: boolean = false): Observable<ExportSpecModel[]> {
        let cacheKey: string = 'exportSpecs'+(filterForAuctions?"1":"0")+(exportFlagsAllowAuctions?"1":"0");
        let cacheMilliseconds: number = 1 * 60 * 60000 * 24; //24 hour
        return this.cache.get(cacheKey, this.getExportSpecs(), cacheMilliseconds);
    }
    
    getExportSpecs(filterForAuctions: boolean = false, exportFlagsAllowAuctions: boolean = false): Observable<ExportSpecModel[]> {
        let httpParams = new HttpParams();
        httpParams = httpParams.set('filterForAuctions',filterForAuctions?"true":"false");
        httpParams = httpParams.set('exportFlagsAllowAuctions',exportFlagsAllowAuctions?"true":"false");
        return this.httpClient.get<ExportSpecModel[]>(Constants.ApiUrl + 'exports/ExportSpecs', { params: httpParams });
    }

    
    scheduleExports(exports:any): Observable<ExportSpecModel[]>
    {
        return this.httpClient.post<ExportSpecModel[]>(Constants.ApiUrl + 'exports/ScheduleExports', exports);
    }

    getExportById(exportSpecId: string): Observable<ExportSpecModel>
    {
        return this.httpClient.get<ExportSpecModel>(Constants.ApiUrl + 'exports/' + exportSpecId);
    }

    saveExportSettings(exportSpecId: string, exportSpec: ExportSpecModel): Observable<ExportSpecModel>
    {
        return this.httpClient.patch<ExportSpecModel>(Constants.ApiUrl + 'exports/' + exportSpecId + '/settings', exportSpec);
    }

    setupExportSettingsFieldGroups(exportSpec: ExportSpecModel, editActive: boolean = false, makeDirty: boolean = false): TabModel[]
    {
        const canManageExport = this.userService.hasUserRight(SecurityRight.Export);
        const fieldGroupTabs: TabModel[] = [];

        if (!canManageExport)
        {
            return fieldGroupTabs;
        }

        const providerMember = exportSpec?.specProvider?.member;
        const hideImagesTab =
            providerMember === ExportSpecProvider.MotorcentralAPI ||
            providerMember === ExportSpecProvider.TradeMe ||
            providerMember === ExportSpecProvider.Facebook ||
            providerMember === ExportSpecProvider.Dealerbase;

        fieldGroupTabs.push(new TabModel("General", exportSpec, this.getExportSettingsFieldGroups(exportSpec, "general", editActive), makeDirty, "general", "", canManageExport, canManageExport));

        if (!hideImagesTab)
        {
            fieldGroupTabs.push(new TabModel("Images", exportSpec, this.getExportSettingsFieldGroups(exportSpec, "images", editActive), makeDirty, "images", "", canManageExport, canManageExport));
        }

        fieldGroupTabs.push(new TabModel("Admin", exportSpec, this.getExportSettingsFieldGroups(exportSpec, "admin", editActive), makeDirty, "admin", "", canManageExport, canManageExport));

        return fieldGroupTabs;
    }

    public getExportSettingsFieldGroups(exportSpec: ExportSpecModel, groupId: string, editActive: boolean): FieldGroupModel[]
    {
        const field_groups: FieldGroupModel[] = [];
        let field_group: FieldGroupModel;

        const providerMember = exportSpec?.specProvider?.member;
        const persistTypeMember = exportSpec?.persistType?.member;
        const extenKey = exportSpec?.extenKey;

        // Provider-level restrictions
        const isProviderRestricted =
            providerMember === ExportSpecProvider.Motorcentral ||
            providerMember === ExportSpecProvider.MotorcentralExternal ||
            providerMember === ExportSpecProvider.AutoBase ||
            providerMember === ExportSpecProvider.DealerPoint ||
            providerMember === ExportSpecProvider.MotorcentralWebsite ||
            providerMember === ExportSpecProvider.MotorcentralAPI ||
            providerMember === ExportSpecProvider.TradeMe ||
            providerMember === ExportSpecProvider.Facebook ||
            providerMember === ExportSpecProvider.Dealerbase;

        // Persist-type checks
        const isFtpPersist = persistTypeMember === ExportPersistType.FTP || persistTypeMember === ExportPersistType.SFTP;
        const isSftpPersist = persistTypeMember === ExportPersistType.SFTP;
        const isS3Persist = persistTypeMember === ExportPersistType.S3;
        const isStoragePersist = persistTypeMember === ExportPersistType.Storage;
        const isDealerbasePersist = persistTypeMember === ExportPersistType.Dealerbase || persistTypeMember === ExportPersistType.DealerbaseSandbox;
        const isTradeMePersist = persistTypeMember === ExportPersistType.TradeMe || persistTypeMember === ExportPersistType.TradeMeSandbox;

        // Real ExtenSpecKey values
        const isVehListTradeMe = extenKey === ExtenSpecKey.VehListTradeMe;
        const isVehListAutobase = extenKey === ExtenSpecKey.VehListAutobase;
        const isVehListDealerbase = extenKey === ExtenSpecKey.VehListDealerbase;
        const isVehListDriven = extenKey === ExtenSpecKey.VehListDriven;
        const isVehListNeedacar = extenKey === ExtenSpecKey.VehListNeedacar;
        const isVehListFacebook = extenKey === ExtenSpecKey.VehListFacebook;

        switch (groupId)
        {
            case "general":
                field_group = new FieldGroupModel("Basic Details");
                field_group.addField(new FieldModel("Provider Type", exportSpec.specProviderId, "lookup", "specProviderId", false, false, exportSpec.specProvider));
                field_group.addField(new FieldModel("Export Title", exportSpec.name, "text", "name", true, true));
                field_group.addField(new FieldModel("Short Description", exportSpec.shortDesc, "text", "shortDesc", true, false));
                field_group.addField(new FieldModel("Last Export Date", exportSpec.lastExportDate, "datetime", "lastExportDate", false, false));
                field_groups.push(field_group);

                field_group = new FieldGroupModel("Export As");
                const formatField = new FieldModel("File Type", exportSpec.formatTypeId, "lookup", "formatTypeId", true, true, exportSpec.formatType);
                formatField.editable = !isProviderRestricted;
                field_group.addField(formatField);

                field_group.addField(new FieldModel("Data to Send", exportSpec.dataFilterId, "lookup", "dataFilterId", true, true, exportSpec.dataFilter));
                field_groups.push(field_group);
                break;

            case "images":
                field_group = new FieldGroupModel("Thumbnail 1");

                const image1Active = new FieldModel("Export Thumbnail 1", exportSpec.image1Active, "toggle", "image1Active", true, false);
                image1Active.hasChildren = true;
                field_group.addField(image1Active);

                const image1Width = new FieldModel("Dimensions (pixels)", exportSpec.image1Width, "number", "image1Width", exportSpec.image1Active, false);
                image1Width.parent = "image1Active";
                image1Width.toggleEditableWhen = "true";
                field_group.addField(image1Width);

                const image1Height = new FieldModel("Dimensions (pixels)", exportSpec.image1Height, "number", "image1Height", exportSpec.image1Active, false);
                image1Height.parent = "image1Active";
                image1Height.toggleEditableWhen = "true";
                field_group.addField(image1Height);

                field_groups.push(field_group);

                field_group = new FieldGroupModel("Thumbnail 2");

                const image2Active = new FieldModel("Export Thumbnail 2", exportSpec.image2Active, "toggle", "image2Active", true, false);
                image2Active.hasChildren = true;
                field_group.addField(image2Active);

                const image2Width = new FieldModel("Dimensions (pixels)", exportSpec.image2Width, "number", "image2Width", exportSpec.image2Active, false);
                image2Width.parent = "image2Active";
                image2Width.toggleEditableWhen = "true";
                field_group.addField(image2Width);

                const image2Height = new FieldModel("Dimensions (pixels)", exportSpec.image2Height, "number", "image2Height", exportSpec.image2Active, false);
                image2Height.parent = "image2Active";
                image2Height.toggleEditableWhen = "true";
                field_group.addField(image2Height);

                field_groups.push(field_group);

                field_group = new FieldGroupModel("Thumbnail 3");

                const image3Active = new FieldModel("Export Thumbnail 3", exportSpec.image3Active, "toggle", "image3Active", true, false);
                image3Active.hasChildren = true;
                field_group.addField(image3Active);

                const image3Width = new FieldModel("Dimensions (pixels)", exportSpec.image3Width, "number", "image3Width", exportSpec.image3Active, false);
                image3Width.parent = "image3Active";
                image3Width.toggleEditableWhen = "true";
                field_group.addField(image3Width);

                const image3Height = new FieldModel("Dimensions (pixels)", exportSpec.image3Height, "number", "image3Height", exportSpec.image3Active, false);
                image3Height.parent = "image3Active";
                image3Height.toggleEditableWhen = "true";
                field_group.addField(image3Height);

                field_groups.push(field_group);
                break;

            case "admin":
                field_group = new FieldGroupModel("Properties");
                const persistField = new FieldModel("Persist type", exportSpec.persistTypeId, "lookup", "persistTypeId", false, false, exportSpec.persistType);
                persistField.editable = !isProviderRestricted;
                field_group.addField(persistField);
                field_group.addField(new FieldModel("Export scheduled", exportSpec.exportScheduled, "boolean", "exportScheduled", false, false));
                field_groups.push(field_group);

                // Delivery Settings: FTP / SFTP
                if (isFtpPersist)
                {
                    field_group = new FieldGroupModel("Delivery Settings");

                    const isZipped = !!exportSpec.persistParam10;
                    const dataFilePathLabel = isZipped ? "Zip File Path" : "Data File Path";

                    field_group.addField(new FieldModel("FTP Server", exportSpec.persistParam1, "text", "persistParam1", true, false));
                    field_group.addField(new FieldModel("FTP Username", exportSpec.persistParam2, "text", "persistParam2", true, false));
                    // field_group.addField(new FieldModel("FTP Password", exportSpec.persistParam3, "text", "persistParam3", true, false));

                    if (!isSftpPersist)
                    {
                        field_group.addField(new FieldModel("Active/Passive Mode", exportSpec.persistParam4, "toggle", "persistParam4", true, false));
                    }

                    field_group.addField(new FieldModel(dataFilePathLabel, exportSpec.persistParam5, "text", "persistParam5", true, false));
                    field_group.addField(new FieldModel("Data File Name", exportSpec.persistParam6, "text", "persistParam6", true, false));

                    const imagePath = new FieldModel("Image File Path", exportSpec.persistParam7, "text", "persistParam7", !isZipped, false);
                    if (isZipped)
                    {
                        imagePath.editable = false;
                    }
                    field_group.addField(imagePath);

                    field_group.addField(new FieldModel("Image File Prefix", exportSpec.persistParam8, "text", "persistParam8", true, false));

                    const zipTransfer = new FieldModel("Send as zipped (compressed) file", exportSpec.persistParam10, "toggle", "persistParam10", true, false);
                    zipTransfer.hasChildren = true;
                    field_group.addField(zipTransfer);

                    const zipFileName = new FieldModel("Zip File Name", exportSpec.persistParam11, "text", "persistParam11", true, false);
                    zipFileName.parent = "persistParam10";
                    zipFileName.toggleEditableWhen = "true";
                    field_group.addField(zipFileName);

                    if (!isSftpPersist)
                    {
                        field_group.addField(new FieldModel("Use SSL", exportSpec.persistParam18, "toggle", "persistParam18", true, false));
                    }

                    field_groups.push(field_group);
                }

                // Delivery Settings: S3
                if (isS3Persist)
                {
                    field_group = new FieldGroupModel("Delivery Settings");
                    field_group.addField(new FieldModel("Bucket", exportSpec.persistTypeS3Bucket, "text", "persistTypeS3Bucket", true, false));
                    field_group.addField(new FieldModel("Folder", exportSpec.persistTypeS3Folder, "text", "persistTypeS3Folder", true, false));
                    field_group.addField(new FieldModel("Region", exportSpec.persistTypeS3Region, "text", "persistTypeS3Region", true, false));
                    field_group.addField(new FieldModel("Access Key ID", exportSpec.persistTypeS3AccessKeyId, "text", "persistTypeS3AccessKeyId", true, false));
                    // field_group.addField(new FieldModel("Secret Access Key", exportSpec.persistTypeS3SecretAccessKey, "text", "persistTypeS3SecretAccessKey", true, false));
                    field_groups.push(field_group);
                }

                // Delivery Settings: Storage
                if (isStoragePersist)
                {
                    field_group = new FieldGroupModel("Delivery Settings");

                    if (providerMember === ExportSpecProvider.MotorcentralWebsite)
                    {
                        field_group.addField(new FieldModel("Website Url", exportSpec.persistTypeStorageWebsiteUrl, "text", "persistTypeStorageWebsiteUrl", true, false));
                    }
                    else
                    {
                        field_group.addField(new FieldModel("Website Vehicle Url", exportSpec.persistTypeStorageVehicleUrl, "text", "persistTypeStorageVehicleUrl", true, false));
                        field_group.addField(new FieldModel("Data File Path", exportSpec.persistTypeStorageDestinationUrl, "text", "persistTypeStorageDestinationUrl", true, false));
                    }

                    field_groups.push(field_group);
                }

                // Delivery Settings: Dealerbase credentials
                // if (isDealerbasePersist)
                // {
                //     field_group = new FieldGroupModel("Delivery Settings");
                //     const isDealerbaseApiExtKey = isVehListDealerbase;
                //     field_group.addField(new FieldModel(isDealerbaseApiExtKey ? "API Username" : "FTP Username", exportSpec.persistParam2, "text", "persistParam2", true, false));
                //     field_group.addField(new FieldModel(isDealerbaseApiExtKey ? "API Password" : "FTP Password", exportSpec.persistParam3, "text", "persistParam3", true, false));
                //     field_groups.push(field_group);
                // }

                // TradeMe persist currently no extra modal/actions in this phase
                if (isTradeMePersist)
                {
                    // intentionally no extra fields/actions yet
                }

                // Export Options
                field_group = new FieldGroupModel("Export Options");

                // Mirrors legacy visibility behavior
                let showExportCIN = true;
                let showExportSummarySheet = true;
                let showExportAlternateVariant = true;
                let showExportAlternateStockNo = true;
                let showExportVehSold = true;
                let showCompletionUri = true;
                let showVehicleUri = true;
                let showHideFeatures = true;
                let showHideTagline = true;
                let showExportPosters = true;
                let showExportVehLimit = true;
                let showExportSpecialPricing = true;

                if (isVehListTradeMe)
                {
                    showExportAlternateStockNo = false;
                    showExportVehSold = false;
                    showCompletionUri = false;
                }
                else
                {
                    showHideTagline = false;
                }

                if (isVehListAutobase || isVehListDealerbase)
                {
                    showCompletionUri = false;
                }
                else
                {
                    showVehicleUri = false;
                    showHideFeatures = false;
                }

                if (!isVehListTradeMe && !isVehListAutobase && !isVehListDealerbase)
                {
                    showExportPosters = false;
                }

                if (!isVehListAutobase && !isVehListDriven && !isVehListDealerbase)
                {
                    showExportVehLimit = false;
                }

                if (isVehListNeedacar)
                {
                    showExportCIN = false;
                    showExportSummarySheet = false;
                    showExportAlternateVariant = false;
                    showExportAlternateStockNo = false;
                    showExportVehSold = false;
                    showExportSpecialPricing = false;
                    showCompletionUri = false;
                }
                else if (isVehListFacebook)
                {
                    showExportCIN = false;
                    showExportSummarySheet = false;
                    showExportAlternateStockNo = false;
                    showCompletionUri = false;
                    showExportVehSold = false;
                }

                if (providerMember === ExportSpecProvider.MotorcentralAPI || providerMember === ExportSpecProvider.MotorcentralWebsite)
                {
                    showCompletionUri = false;
                }

                if (isVehListDealerbase)
                {
                    showCompletionUri = false;
                }

                if (showExportCIN)
                {
                    field_group.addField(new FieldModel("Export Consumer Information Notice", exportSpec.exportFlagsCIN, "toggle", "exportFlagsCIN", true, false));
                }

                if (showExportSummarySheet)
                {
                    field_group.addField(new FieldModel("Export Vehicle Summary Sheet", exportSpec.exportFlagsSummarySheet, "toggle", "exportFlagsSummarySheet", true, false));
                }

                if (showExportAlternateVariant)
                {
                    field_group.addField(new FieldModel("Export Alternate Variant", exportSpec.exportFlagsAltVariant, "toggle", "exportFlagsAltVariant", true, false));
                }

                if (showExportAlternateStockNo)
                {
                    field_group.addField(new FieldModel("Export Alternate Stock No", exportSpec.exportFlagsAltStockNo, "toggle", "exportFlagsAltStockNo", true, false));
                }

                if (showExportVehSold)
                {
                    const soldDays = (exportSpec.exportVehSold || 0);
                    const vehSoldCheckbox = new FieldModel("Export Recently Sold Vehicles", soldDays > 0, "toggle", "exportVehSoldCheckbox", true, false);
                    vehSoldCheckbox.hasChildren = true;
                    field_group.addField(vehSoldCheckbox);

                    const vehSoldDays = new FieldModel("Sold in the Last (Days)", soldDays, "number", "exportVehSold", soldDays > 0, false);
                    vehSoldDays.parent = "exportVehSoldCheckbox";
                    vehSoldDays.toggleEditableWhen = "true";
                    field_group.addField(vehSoldDays);
                }

                // Positive UI labels with inverse flag storage
                field_group.addField(new FieldModel("Export Image Overlays", !exportSpec.exportFlagsIgnoreOverlays, "toggle", "exportFlagsIgnoreOverlays", true, false));

                if (showExportSpecialPricing)
                {
                    field_group.addField(new FieldModel("Export Special Pricing", !exportSpec.exportFlagsIgnoreSpecialPrice, "toggle", "exportFlagsIgnoreSpecialPrice", true, false));
                }

                if (showExportPosters)
                {
                    field_group.addField(new FieldModel("Export Image Posters", !exportSpec.exportFlagsIgnorePosters, "toggle", "exportFlagsIgnorePosters", true, false));
                }

                field_group.addField(new FieldModel("Export In Progress Sales", exportSpec.exportFlagsIncludeInProgressSales, "toggle", "exportFlagsIncludeInProgressSales", true, false));

                var exportInProgressSalesAsOptions =
                [
                    new ListItemModel("30", "Sold"),
                    new ListItemModel("20", "Unsold")
                ];

                field_group.addField(new FieldModel("In Progress Sales As", exportSpec.exportInProgressSalesAs, "select", "exportInProgressSalesAs", true, false, exportInProgressSalesAsOptions));

                if (showCompletionUri)
                {
                    field_group.addField(new FieldModel("Completion Uri", exportSpec.persistTypeFTPCompletionUri, "text", "persistTypeFTPCompletionUri", true, false));
                }

                if (showVehicleUri)
                {
                    field_group.addField(new FieldModel("Vehicle Uri", exportSpec.persistTypeFTPVehicleUriFormat, "text", "persistTypeFTPVehicleUriFormat", true, false));
                }

                if (showHideFeatures)
                {
                    field_group.addField(new FieldModel("Hide Features", exportSpec.exportFlagsHideFeatures, "toggle", "exportFlagsHideFeatures", true, false));
                }

                if (showHideTagline)
                {
                    field_group.addField(new FieldModel("Hide Tagline", exportSpec.exportFlagsHideTagline, "toggle", "exportFlagsHideTagline", true, false));
                }

                if (showExportVehLimit)
                {
                    const vehLimit = (exportSpec.exportVehLimit || 0);
                    const vehLimitCheckbox = new FieldModel("Limit Vehicles Exported", vehLimit > 0, "toggle", "exportVehLimitCheckbox", false, false);
                    vehLimitCheckbox.hasChildren = true;
                    field_group.addField(vehLimitCheckbox);

                    const vehLimitCount = new FieldModel("Vehicles exported limit", vehLimit, "number", "exportVehLimit", false, false);
                    vehLimitCount.parent = "exportVehLimitCheckbox";
                    vehLimitCount.toggleEditableWhen = "true";
                    field_group.addField(vehLimitCount);
                    
                    if (!editActive && isVehListDealerbase)
                    {
                        var vehDealershipLimits = new FieldModel('Vehicle dealership limits', '', 'button', 'viewVehicleDealershipLimits', false, false, null, "button-veh-dealership-limits");
                        vehDealershipLimits.buttonExpand = 'block';
                        vehDealershipLimits.buttonSize = 'small';
                        field_group.addField(vehDealershipLimits);
                    }
                }

                field_groups.push(field_group);
                break;
        }

        return field_groups;
    }

}
